"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DpStudentAndClassPerformance = void 0;
var jsx_runtime_1 = require("react/jsx-runtime");
var react_1 = require("react");
var styled_components_1 = require("styled-components");
var react_i18next_1 = require("react-i18next");
var intersectionWith_1 = __importDefault(require("lodash/intersectionWith"));
var truncate_1 = __importDefault(require("lodash/truncate"));
var DpSectionCard_1 = require("../Cards/DpSectionCard");
var DpBarChartGrouped_1 = require("../BarChartGrouped/DpBarChartGrouped");
var chartTypes_enum_1 = require("../Charts/chartTypes.enum");
var api_1 = require("../../../api");
var DpStudentAndClassPerformanceHeader_1 = __importDefault(require("./DpStudentAndClassPerformanceHeader"));
var DpStudentAndClassPerformance_styled_1 = require("./DpStudentAndClassPerformance.styled");
var baseDataset = {
    type: 'bar',
    maxBarThickness: 15,
    minBarThickness: 15,
    barPercentage: 0.9,
    categoryPercentage: 0.3,
};
var DpStudentAndClassPerformance = function (_a) {
    var _b = _a.studentPerformance, studentPerformance = _b === void 0 ? [] : _b, _c = _a.classPerformance, classPerformance = _c === void 0 ? [] : _c;
    // --------------------------- Const
    var t = (0, react_i18next_1.useTranslation)('common').t;
    // --------------------------- Context
    var themeContext = (0, react_1.useContext)(styled_components_1.ThemeContext);
    // --------------------------- State
    var _d = (0, react_1.useState)(false), initialized = _d[0], setInitialized = _d[1];
    // --------------------------- Methods
    /**
     * Takes studentPerformance and classPerformance arrays and
     * returns an array of string with the intersection between them.
     */
    var performanceIntersection = (0, react_1.useMemo)(function () {
        return (0, intersectionWith_1.default)(studentPerformance, classPerformance, function (student, aClass) { return student.assessment.id === aClass.assessment.id; })
            .map(function (item) { return item.assessment; })
            .slice(0, 9);
    }, [studentPerformance, classPerformance]);
    /**
     * Takes performanceIntersection array and each string is splitted for multiline purpuses,
     * each value is a text line. The second line will be trim if it exceeds 13 characters
     * and ellipsis will be added at the end.
     */
    var labels = (0, react_1.useMemo)(function () {
        return performanceIntersection.map(function (assessment) {
            var titleSplitted = assessment.assessmentTemplate.title.split(' ');
            return [
                titleSplitted.slice(0, 6).join(' '),
                (0, truncate_1.default)(titleSplitted.slice(6, titleSplitted.length).join(' '), {
                    length: 35,
                    separator: ' ',
                }),
            ];
        });
    }, [performanceIntersection]);
    var classPerformances = (0, react_1.useMemo)(function () {
        return classPerformance
            .filter(function (item) {
            return performanceIntersection.some(function (assessment) { return assessment.id === item.assessment.id; });
        })
            .map(function (_a) {
            var percentage = _a.percentage;
            return percentage;
        });
    }, [performanceIntersection, classPerformance]);
    var studentPerformances = (0, react_1.useMemo)(function () {
        return studentPerformance
            .filter(function (item) {
            return performanceIntersection.some(function (assessment) { return assessment.id === item.assessment.id; });
        })
            .map(function (_a) {
            var percentage = _a.percentage;
            return percentage;
        });
    }, [performanceIntersection, studentPerformance]);
    var studentColor = (0, react_1.useMemo)(function () {
        return (0, api_1.findPerformanceBandColor)(themeContext, (0, api_1.getCurriculumPrefixFromCurriculum)(api_1.CurriculumId.WitAndWisdom), 'bandE');
    }, [themeContext]);
    var classColor = (0, react_1.useMemo)(function () {
        return (0, api_1.findPerformanceBandColor)(themeContext, (0, api_1.getCurriculumPrefixFromCurriculum)(api_1.CurriculumId.WitAndWisdom), 'bandA');
    }, [themeContext]);
    var baseFontStyles = (0, react_1.useMemo)(function () { return ({
        fontFamily: 'Circular Std',
        fontColor: themeContext.Colors.Typography.primary,
    }); }, [themeContext]);
    var datasets = (0, react_1.useMemo)(function () { return [
        __assign({ data: studentPerformances, backgroundColor: studentColor, hoverBackgroundColor: studentColor }, baseDataset),
        __assign({ data: classPerformances, backgroundColor: classColor, hoverBackgroundColor: classColor }, baseDataset),
    ]; }, [studentPerformances, classPerformances, studentColor, classColor]);
    var chartOptions = (0, react_1.useMemo)(function () { return ({
        scales: {
            yAxes: [
                {
                    scaleLabel: __assign({ display: true, labelString: t('studentAndClassPerformance.averageScore'), fontStyle: 'bold' }, baseFontStyles),
                    ticks: __assign(__assign({}, baseFontStyles), { fontStyle: 'normal' }),
                },
            ],
            xAxes: [
                {
                    ticks: __assign(__assign({}, baseFontStyles), { fontStyle: 'normal', fontSize: '10' }),
                },
            ],
        },
    }); }, [baseFontStyles, t]);
    // --------------------------- Methods
    (0, api_1.useMount)(function () {
        setInitialized(true);
    });
    return (initialized && ((0, jsx_runtime_1.jsx)(DpSectionCard_1.DpSectionCard, { title: (0, jsx_runtime_1.jsx)(DpStudentAndClassPerformanceHeader_1.default, {}), children: (0, jsx_runtime_1.jsx)(DpStudentAndClassPerformance_styled_1.StyledStudentAndClassPerformance, { children: (0, jsx_runtime_1.jsx)("div", { className: "report", children: (0, jsx_runtime_1.jsx)(DpBarChartGrouped_1.DpBarChartGrouped, { labels: labels, type: chartTypes_enum_1.LibraryChartTypes.Bar, id: "grouped-student-class-performance", ariaLabel: t('studentAndClassPerformance.ariaLabel'), datasets: datasets, options: chartOptions }) }) }) })));
};
exports.DpStudentAndClassPerformance = DpStudentAndClassPerformance;
exports.default = exports.DpStudentAndClassPerformance;
